// /app/frontend/src/components/RecognitionBadgeCard.jsx
// PATCH v2: Remove dead button affordance, add info tooltip
// Badges are NOT clickable - just display chips

import React, { useState } from 'react';
import { Award, Lock, Info, X } from 'lucide-react';
import { useRecognitionSummary } from '../hooks/useRecognition';

/**
 * RecognitionBadgeCard - Read-only recognition badges display
 * PATCH v2: No click affordance on badges, info icon with tooltip
 */
export default function RecognitionBadgeCard({ compact = false, showProgress = true }) {
  const { badges, progress, stats, loading, error } = useRecognitionSummary();
  const [showInfo, setShowInfo] = useState(false);

  if (loading) {
    return (
      <div className="rounded-lg surface-card px-3 py-2">
        <div className="animate-pulse flex items-center gap-2">
          <div className="w-4 h-4 bg-gray-200 dark:bg-slate-700 rounded"></div>
          <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded w-20"></div>
        </div>
      </div>
    );
  }

  if (error) return null;

  const hasBadges = badges.length > 0;
  const hasProgress = progress.length > 0;

  // Compact inline view - no hover/click
  if (compact) {
    if (!hasBadges) return null;
    return (
      <div className="flex items-center gap-1 flex-wrap">
        {badges.map((badge) => (
          <span
            key={badge.key}
            className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 select-none"
          >
            <span>{badge.emoji}</span>
            <span>{badge.label}</span>
          </span>
        ))}
      </div>
    );
  }

  return (
    <div className="rounded-lg surface-card px-3 py-2 shadow-sm" data-testid="recognition-badge-card">
      {/* Header with info icon */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Award className="w-4 h-4 text-amber-500" />
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Recognition</span>
          {hasBadges && (
            <span className="text-xs text-gray-400 dark:text-gray-500">({badges.length})</span>
          )}
        </div>
        <button
          onClick={() => setShowInfo(true)}
          className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-slate-800 transition"
          aria-label="What are badges?"
          data-testid="recognition-info-btn"
        >
          <Info className="w-3.5 h-3.5 text-gray-400" />
        </button>
      </div>

      {/* Earned Badges - READ ONLY, no hover effects, no cursor pointer */}
      {hasBadges ? (
        <div className="flex gap-1.5 flex-wrap">
          {badges.map((badge) => (
            <span
              key={badge.key}
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 select-none"
              data-testid={`badge-${badge.key}`}
            >
              <span className="text-sm">{badge.emoji}</span>
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{badge.label}</span>
            </span>
          ))}
        </div>
      ) : (
        <p className="text-xs text-gray-400 dark:text-gray-500">No badges yet</p>
      )}

      {/* Progress - READ ONLY text, not clickable */}
      {showProgress && hasProgress && (
        <div className="mt-2 pt-2 border-t border-gray-100 dark:border-slate-700/50">
          <div className="text-xs text-gray-400 dark:text-gray-500">
            {progress.slice(0, 2).map((item, idx) => (
              <span key={item.badge_key} className="inline-flex items-center gap-1 mr-3">
                <Lock className="w-3 h-3" />
                <span className="text-amber-600 dark:text-amber-400">{item.current}/{item.target}</span>
                <span className="truncate max-w-[50px]">{item.label}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Info Modal/Tooltip */}
      {showInfo && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" onClick={() => setShowInfo(false)}>
          <div 
            className="bg-white dark:bg-slate-900 rounded-xl p-4 max-w-xs w-full shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">What are Badges?</h4>
              <button
                onClick={() => setShowInfo(false)}
                className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-slate-800"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
              <p>
                Badges are <strong>private recognition</strong> for your engagement in the Unity community.
              </p>
              <p>
                They&apos;re earned automatically as you participate - visiting rooms, responding to prompts, and more.
              </p>
              <p className="text-xs text-gray-400">
                Only you can see your badges. They&apos;re a personal reminder of your journey.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
